import type { FiatFlow, FormValues, JsonValue } from '@funkit/connect-core'
import { formValuesInput } from '@funkit/connect-core'
import type { TFunction } from 'i18next'

import type { FiatFormScreenProps } from './FiatFormScreen'
import { roundErrorMessage } from './stepDetails'

/**
 * Binds a `KYC{INPUT_REQUIRED}` step response to the form screen.
 *
 * Every answer rides in one transition input, so a keystroke rewrites that
 * input's object rather than adding a key of its own — the body the contract
 * declares is `{ formId, <values> }`, never a flattened field per key.
 *
 * `undefined` means the form is unrenderable: no page to walk, or no CTA to
 * send it with.
 */
export function formScreenProps(
  flow: FiatFlow,
  t: TFunction,
  /** The buyer's region, ISO 3166-1 alpha-2, when the lookup has answered. */
  defaultCountry?: string,
  /** What earlier rounds of this session already collected; see `carriedValues`. */
  carriedValues?: FormValues,
): Omit<FiatFormScreenProps, 'onClose'> | undefined {
  const state = flow.stepResponse?.state
  if (state?.kind !== 'KYC' || state.phase !== 'INPUT_REQUIRED') {
    return undefined
  }

  const cta = flow.ctas[0]
  if (cta === undefined) {
    return undefined
  }

  const input = formValuesInput(cta)
  if (input === undefined) {
    return undefined
  }

  const values = collectedValues(flow.collected[input])

  return {
    form: state.form,
    // Discovery KYC can precede pricing; post-quote KYC retains its quote.
    quote: state.quote,
    // The round names who settles it; the consent screen names them back.
    provider: flow.stepResponse?.provider,
    values,
    defaultCountry,
    ...(carriedValues === undefined ? {} : { carriedValues }),
    errorMessage: roundErrorMessage(
      state.error,
      t,
      flow.stepResponse?.provider,
    ),
    isSubmitting: flow.isInFlight,
    onChangeField: (fieldId, value) =>
      flow.setField(input, { ...values, [fieldId]: value }),
    // One write for every prefilled field: a call per field would each spread
    // the same stale `values` and the last would win.
    onPrefill: (prefilled) => flow.setField(input, { ...values, ...prefilled }),
    onSubmit: () => void flow.submit(cta),
  }
}

function collectedValues(collected: JsonValue | undefined): FormValues {
  return collected !== null &&
    typeof collected === 'object' &&
    !Array.isArray(collected)
    ? collected
    : {}
}
